/**
 * Capturing what is on screen.
 *
 * This was a notification saying the feature "lands in v2" — a menu entry that
 * looked like a function and was an apology. The plumbing for it already
 * existed: `nvRegistry`'s own comment lists snapshot as one of the reasons it
 * keeps the live instances.
 *
 * The one subtlety is WebGL. A canvas without `preserveDrawingBuffer` has its
 * drawing buffer cleared once the browser composites the frame, so reading it
 * later returns transparent black — the classic "my screenshot is empty". The
 * read therefore happens immediately after a synchronous redraw, in the same
 * task, before the compositor can run.
 */

import { getInstance, listInstances } from "./nvRegistry";

export interface Capture {
  dataUrl: string;
  width: number;
  height: number;
}

/**
 * Draw and read one instance's canvas.
 *
 * Returns `null` rather than a blank image when the canvas is not there or the
 * read is refused — a file full of transparent pixels is worse than being told
 * it did not work.
 */
/** A redrawn canvas, still live, so it can be composited without decoding. */
function drawn(nv: ReturnType<typeof getInstance>): HTMLCanvasElement | null {
  const canvas = nv?.canvas;
  if (!nv || !canvas || canvas.width === 0 || canvas.height === 0) return null;
  try {
    // Synchronous redraw: see the note above about the drawing buffer.
    nv.drawScene();
    return canvas;
  } catch {
    return null;
  }
}

function toCapture(canvas: HTMLCanvasElement): Capture | null {
  try {
    const dataUrl = canvas.toDataURL("image/png");
    // A canvas that drew nothing still yields a valid but tiny data URL.
    if (!dataUrl.startsWith("data:image/png") || dataUrl.length < 128) return null;
    return { dataUrl, width: canvas.width, height: canvas.height };
  } catch {
    return null;
  }
}

function capture(nv: ReturnType<typeof getInstance>): Capture | null {
  const canvas = drawn(nv);
  return canvas ? toCapture(canvas) : null;
}

/** The pane to capture: the named one, else the first that yields an image. */
export function captureViewport(paneId?: string): Capture | null {
  if (paneId) return capture(getInstance(paneId));
  for (const nv of listInstances()) {
    const shot = capture(nv);
    if (shot) return shot;
  }
  return null;
}

/**
 * Every live pane, side by side, in the order they were registered.
 *
 * A 2×2 layout is four images of one moment, and a reader who asks for a
 * snapshot of it means the arrangement, not one tile of it.
 */
export function captureAllViewports(): Capture | null {
  // The live canvases, not their data URLs. Going via an `Image` needs a
  // decode, and `complete` is false straight after assigning a data URL in
  // WebKit — which drew nothing and silently fell back to a single pane.
  // `drawImage` takes a canvas directly and is synchronous.
  const canvases = listInstances()
    .map((nv) => drawn(nv))
    .filter((c): c is HTMLCanvasElement => c !== null);
  if (canvases.length === 0) return null;
  if (canvases.length === 1) return toCapture(canvases[0]);

  const gap = 8;
  const height = Math.max(...canvases.map((c) => c.height));
  const width = canvases.reduce((w, c) => w + c.width, 0) + gap * (canvases.length - 1);
  const sheet = document.createElement("canvas");
  sheet.width = width;
  sheet.height = height;
  const ctx = sheet.getContext("2d");
  if (!ctx) return toCapture(canvases[0]);
  // Medical images are read on black, and so is the gap between them.
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, width, height);

  let x = 0;
  for (const canvas of canvases) {
    try {
      ctx.drawImage(canvas, x, 0);
    } catch {
      // One unreadable pane must not lose the others.
    }
    x += canvas.width + gap;
  }
  return toCapture(sheet);
}

/** `sub-02e8eb42_axial_20260920-161530.png` */
export function snapshotFileName(subjectId: string | null, plane: string | null): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return [subjectId || "viewport", plane || null, stamp].filter(Boolean).join("_") + ".png";
}
